// @todo: refactor (too long, move handler functions)
import React, { useEffect, useCallback } from 'react';
import { Network } from 'vis-network';
import { useSelector, useDispatch } from 'react-redux';
import { DataSet } from 'vis-data';
import debounce from 'lodash/debounce';
import 'vis-network/styles/vis-network.min.css';
import { updateNode, setNodePositions } from '../features/networkUndoSlice';
import {
  setSelectedNode, // TODO: rename, should be "editing node"
  setSelectedEdge,
  setHighlightedNodes, // TODO: rename, should be "selected node"
  setHighlightedEdges,
  setSelfReference,
} from '../features/networkSlice';
import { DEFAULT_OPTIONS } from '../lib/network/config';
import EditNodeDialog from './EditNodeDialog';
import EditEdgeDialog from './EditEdgeDialog';
import { handleDelete, handleNewNode, handleNewEdge } from '../lib/network/handlers';

export default function NetworkPanel(props) {
  const dispatch = useDispatch();

  // component state
  const { network } = props;

  // global state
  const tool = useSelector((state) => state.toolbar.tool);
  const nodes = new DataSet(useSelector((state) => state.networkUndo.present.nodes));
  const edgesData = useSelector((state) => state.networkUndo.present.edges);
  const physics = useSelector((state) => state.network.physics);
  const selfReference = useSelector((state) => state.network.selfReference);
  const edges = new DataSet(selfReference ? edgesData : edgesData.map((edge) => (
    edge.from === edge.to ? { ...edge, hidden: true } : edge
  )));

  const handleClickEvent = (event) => {
    if (event) {
      if (tool === 'delete') {
        handleDelete(event, dispatch);
      }
      if (tool === 'newNode') {
        const nodeId = nodes.max('id') ? nodes.max('id').id + 1 : 1;
        handleNewNode(event, dispatch, nodeId);
      }
    }
  };

  const handleToolChange = () => {
    if (network.current) {
      if (['edgePos', 'edgeNeg'].includes(tool)) {
        network.current.addEdgeMode();
      } else {
        network.current.addNodeMode();
      }
    }
  };

  const toggleEditing = (event) => {
    if (event.nodes.length > 0) {
      dispatch((setSelectedNode(nodes.get(event.nodes[0]))));
    } else if (event.edges.length > 0) {
      dispatch(setSelectedEdge(edges.get(event.edges[0])));
    }
  };

  const togglePhysics = () => {
    network.current.setOptions({ physics });
  };

  const storePositionsDebounced = useCallback(
    debounce(
      () => {
        const positions = network.current.getPositions();
        dispatch(setNodePositions(positions));
      },
      1000,
    ),
    [],
  );

  const renderNetwork = () => {
    const container = document.getElementById('network');
    const networkData = { nodes, edges };
    const options = DEFAULT_OPTIONS;

    // current viewport
    const prevScale = network.current ? network.current.getScale() : null;
    const prevPosition = network.current ? network.current.getViewPosition() : null;

    // note: `callback` param is needed for Vis to work (addNode, etc)
    /* eslint-disable no-unused-vars */
    options.manipulation.addNode = (data, callback) => {
      network.current.addNodeMode();
    };
    options.manipulation.addEdge = (data, callback) => {
      if (data.from === data.to) {
        dispatch(setSelfReference(true));
      }
      handleNewEdge(dispatch, tool, data, edges);
      network.current.addEdgeMode();
    };
    // options.physics = physics; // slow and buggy

    // network
    network.current = new Network(container, networkData, options);

    // physics (needs to be toggled _after_ render)
    togglePhysics();

    // restore viewport
    if (prevScale && prevPosition) {
      network.current.moveTo({ position: prevPosition, scale: prevScale });
    } else {
      // TODO: gotta reset `prevScale` and `prevPosition` to trigger this
      network.current.fit();
    }

    // toggle network mode
    handleToolChange();

    // event handlers
    network.current.on('click', handleClickEvent);
    network.current.on('dragEnd', (event) => {
      if (event.nodes.length > 0) {
        const node = nodes.get(event.nodes[0]);
        if (!network.current.physics.options.enabled) {
          dispatch(updateNode({
            id: node.id,
            x: Math.round(event.pointer.canvas.x / 50) * 50,
            y: Math.round(event.pointer.canvas.y / 50) * 50,
          }));
        }
      }
    });
    network.current.on('stabilized', (event) => {
      // limit storing positions (based on network size to reduce re-draws)
      if (event.iterations > nodes.length) {
        storePositionsDebounced();
      }
    });
    network.current.on('doubleClick', (event) => { // double click
      if (tool === 'select') {
        toggleEditing(event);
      }
    });
    network.current.on('oncontext', (event) => { // right click
      const eventObj = event;
      const nodeAtPointer = network.current.getNodeAt({
        x: event.pointer.DOM.x,
        y: event.pointer.DOM.y,
      });
      const edgeAtPointer = network.current.getEdgeAt({
        x: event.pointer.DOM.x,
        y: event.pointer.DOM.y,
      });
      eventObj.nodes = nodeAtPointer ? [nodeAtPointer] : [];
      eventObj.edges = edgeAtPointer ? [edgeAtPointer] : [];
      toggleEditing(eventObj);
      // disable context menu when node/edge clicked
      if (eventObj.nodes.length > 0 || eventObj.edges.length > 0) {
        event.event.preventDefault();
      }
    });
    network.current.on('selectNode', (event) => {
      dispatch(setHighlightedNodes(event.nodes));
      dispatch(setHighlightedEdges(event.edges));
    });
    network.current.on('deselectNode', (event) => {
      dispatch(setHighlightedNodes([]));
    });
    network.current.on('selectEdge', (event) => {
      dispatch(setHighlightedNodes(event.nodes));
      dispatch(setHighlightedEdges(event.edges));
    });
    network.current.on('deselectEdge', (event) => {
      dispatch(setHighlightedEdges([]));
    });
  };

  // effects
  useEffect(handleToolChange, [tool]);
  useEffect(renderNetwork, [nodes, edges]);
  useEffect(togglePhysics, [physics]);
  useEffect(() => {
    const handleKeyDown = (event) => {
      // prevent triggering when typing
      const { activeElement } = document;
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(activeElement.tagName)
        || activeElement.isContentEditable
      ) return;

      if (event.key === 'Delete') {
        const deleteEvent = {
          nodes: network.current.getSelectedNodes(),
          edges: network.current.getSelectedEdges(),
        };
        handleDelete(deleteEvent, dispatch);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  });

  return (
    <>
      <EditNodeDialog network={network} />
      <EditEdgeDialog />
      <div id="network" style={{ width: '100%', height: '98vh' }} />
    </>
  );
}
